import { useCallback, useEffect, useRef, useState } from 'react'
import fetchDemoMaterialRequest from 'app/apis/fetchDemoMaterialRequest'
import ToastAndroid from 'app/mocks/ToastAndroid'
import { DemoMaterialRequest } from 'app/models/RequestFlow'
import { fixNextPageUrl } from 'app/utils/helpers/fixNextPageUrl'

const useDemoMaterialRequest = ({
  request_type,
  status,
  lang = 'en',
  roleType,
  created_at_after,
  created_at_before,
  enabled,
}: {
  request_type?: string
  status?: string
  lang?: string
  roleType?: string
  created_at_after?: string
  created_at_before?: string
  enabled?: boolean
}) => {
  const [request, setRequest] = useState<DemoMaterialRequest[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingNext, setLoadingNext] = useState(false)
  const [page, setPage] = useState(1)
  const [nextPageUrl, setNextPageUrl] = useState<string | null>('')
  const isFetchingNextRef = useRef(false)

  const fetchNext = useCallback(async () => {
    if (!enabled || !roleType || !nextPageUrl || loadingNext || isFetchingNextRef.current)
      return
    try {
      isFetchingNextRef.current = true
      setLoadingNext(true)
      const response = await fetchDemoMaterialRequest(
        page + 1,
        request_type,
        status,
        roleType,
        created_at_after,
        created_at_before,
        lang,
      )
      if (response) {
        setRequest(prev => {
          const existingIds = new Set(prev.map(item => item.id))
          const incoming = (response.results || []).filter(
            item => !existingIds.has(item.id),
          )
          return [...prev, ...incoming]
        })
        setPage(prev => prev + 1)
        setNextPageUrl(fixNextPageUrl(response.next) || null)
      }
    } catch {
      ToastAndroid.show('Something went wrong', ToastAndroid.SHORT)
    } finally {
      isFetchingNextRef.current = false
      setLoadingNext(false)
    }
  }, [
    enabled,
    roleType,
    nextPageUrl,
    page,
    request_type,
    status,
    created_at_after,
    created_at_before,
    lang,
    loadingNext,
  ])

  const getRequest = useCallback(async () => {
    if (!enabled || !roleType) return
    setLoading(true)
    try {
      const response = await fetchDemoMaterialRequest(
        1,
        request_type,
        status,
        roleType,
        created_at_after,
        created_at_before,
        lang,
      )
      if (response) {
        setRequest(response.results)
        setNextPageUrl(fixNextPageUrl(response.next) || null)
        setPage(1)
      }
    } catch {
      ToastAndroid.show('Something went wrong', ToastAndroid.SHORT)
    } finally {
      setLoading(false)
    }
  }, [
    request_type,
    status,
    roleType,
    created_at_after,
    created_at_before,
    enabled,
    lang,
  ])

  useEffect(() => {
    if (enabled) getRequest()
  }, [enabled, getRequest, roleType])

  return {
    enabled,
    fetchNext,
    request,
    loading,
    loadingNext,
    getRequest,
  }
}

export default useDemoMaterialRequest
